// A return step waiting on the OMS API, on the panels (2026-10-29,
// docs/contact-view.md "A step waiting on the OMS API";
// server/src/routes/return-labels.js). Pure, so it is unit-testable.

import { CHECK_STATE_ICON, PENDING_CHIP_TEXT, PENDING_KIND_RMA_NUMBER, PENDING_KIND_ZERO_DOLLAR, applyZeroDollarLabelSentence, formatEtTime, zeroDollarLabelOn, zeroDollarRowSendsLabel, replacementLabelOn } from '@cxhub/shared'

// The step types whose Process creates an RMA and may wait up to 5 s for its
// number ("Waiting for the OMS API to assign the RMA number…").
export const RMA_NUMBER_WAIT_STEP_TYPES = Object.freeze(['process_return_refund', 'resend_return_label', 'automated_return_notice_create_rma', 'automated_return_notice_resend_label'])

export function waitsForRmaNumber(action) {
  return RMA_NUMBER_WAIT_STEP_TYPES.includes(action?.type)
}

// The contact's row for one step (the newest), or null.
export function pendingStepFor(data, suggestionId, index) {
  if (!data || !suggestionId) return null
  return (data.steps ?? []).find((s) => s?.suggestionId === suggestionId && s?.stepIndex === index) ?? null
}

// A row-like view of a step result that waits (right after Process, before
// the contact's rows are read again).
export function pendingFromResult(result) {
  if (!result || !(result.labelPending || result.zeroDollarPending)) return null
  const kind = result.zeroDollarPending ? PENDING_KIND_ZERO_DOLLAR : PENDING_KIND_RMA_NUMBER
  return { id: result.pendingReturnLabelId ?? null, kind, status: 'waiting', chip: PENDING_CHIP_TEXT[kind], checklist: Array.isArray(result.checklist) ? result.checklist : [] }
}

// One checklist line as shown: icon, text, the id and time.
export function checklistLineText(line) {
  const icon = CHECK_STATE_ICON[line?.state] ?? '•'
  const parts = [line?.label ?? line?.key ?? '']
  if (line?.id && !String(line.label ?? '').includes(String(line.id))) parts.push(`(${line.id})`)
  let text = `${icon} ${parts.join(' ')}`
  const at = formatEtTime(line?.at)
  if (line?.state === 'done' && at) text += `, ${at}`
  if (line?.after) text += ` ${line.after}`
  if (line?.detail && line.state !== 'done') text += ` — ${line.detail}`
  if (line?.state === 'failed' && line?.error) text += ` — ${line.error}`
  return text
}

// "Record the label again" (2026-11-02): the confirm's words. info: the
// row's recordAgain ({ trackingNumbers, erpRmaId, channelKind }); related:
// the contact's related contacts ({ id, convNumber }), the same list the
// contact view's "Same request as #… / related" links come from. The
// related line shows only when there are some.
export const RECORD_AGAIN_TITLE = 'Send the return label?'
export const RECORD_AGAIN_BUTTON = 'Record the label again'
export function recordAgainConfirm(info, related = [], { selfId = null } = {}) {
  const tns = (info?.trackingNumbers ?? []).join(', ')
  const rma = info?.erpRmaId ?? 'the RMA'
  const body = info?.channelKind === 'ebay'
    ? `This records label ${tns} on ${rma} and sends it to the buyer on eBay. No new label is made.`
    : `This records label ${tns} on ${rma} and the Console emails it to the customer at the order's email. No new label is made.`
  const seen = new Set()
  const links = []
  for (const m of related ?? []) {
    if (!m?.id || m.id === selfId || seen.has(m.id)) continue
    seen.add(m.id)
    links.push({ id: m.id, convNumber: m.convNumber ?? null })
  }
  links.sort((a, b) => Number(a.convNumber ?? 0) - Number(b.convNumber ?? 0))
  return {
    title: RECORD_AGAIN_TITLE,
    body,
    relatedLead: links.length ? 'Before sending: check the customer still needs to send the parts back. Related contacts:' : null,
    related: links,
  }
}

// The chip of a row that is no longer waiting.
export function finishedChipText(row) {
  if (!row) return null
  // A $0 RMA watch ended because no label was requested (2026-11-02).
  if (row.status === 'done' && row.endedAs === 'not_needed') return 'Not needed: no return label requested'
  if (row.status === 'done') return 'Finished by CXHub'
  if (row.status === 'timed_out') return 'Timed out: back in the queue'
  if (row.status === 'failed') return 'Not finished: back in the queue'
  return null
}

// The banner lines for the contact: other contacts' waiting steps on the
// same order, once each.
export function orderWaitingBanners(data) {
  const seen = new Set()
  const out = []
  for (const r of data?.orderWaiting ?? []) {
    if (!r?.banner || seen.has(r.banner)) continue
    seen.add(r.banner)
    out.push(r.banner)
  }
  return out
}

// What's next lines for this contact's timed-out waits.
export function timedOutNextSteps(data) {
  return (data?.steps ?? []).filter((s) => s?.status === 'timed_out' && s?.nextStep).map((s) => s.nextStep)
}

// The step's chosen reason code requires a $0 RMA (the catalog's flag on
// the step's options; never a list of codes).
export function zeroDollarOption(options, reasonCode) {
  if (reasonCode == null || reasonCode === '') return null
  const opt = (options ?? []).find((o) => String(o?.value) === String(reasonCode))
  return opt?.requiresZeroDollarRma ? opt : null
}

// The optional "create and send a return label" step from a step's input:
// on only when a person turned it on (2026-10-30, @cxhub/shared's
// zeroDollarLabelOn(); off by default).
export function zeroDollarLabelChecked(input) {
  return zeroDollarLabelOn(input)
}

// The step input after the optional step changes: on records who turned it
// on and when (kept on the step, sent with Process); off clears them.
export function withZeroDollarLabelChoice(input, checked, { by = null, at = new Date().toISOString() } = {}) {
  const next = { ...(input ?? {}) }
  if (checked) {
    next.zeroDollarLabel = true
    next.zeroDollarLabelBy = by
    next.zeroDollarLabelAt = at
  } else {
    delete next.zeroDollarLabel
    delete next.zeroDollarLabelBy
    delete next.zeroDollarLabelAt
  }
  return next
}

// The step input with the choice cleared (a new reason code starts off
// again).
export function withoutZeroDollarChoice(input) {
  const next = { ...(input ?? {}) }
  delete next.zeroDollarLabel
  delete next.zeroDollarLabelBy
  delete next.zeroDollarLabelAt
  return next
}

// The drafted reply after the box changes (or the reason code moves between
// a $0 RMA code and one without): the label sentence in or out, the other
// edits kept.
export function draftWithZeroDollarChoice(draft, { zeroDollar, checked, channelKind }) {
  return applyZeroDollarLabelSentence(draft, { checked: Boolean(zeroDollar) && Boolean(checked), channelKind })
}

// The Activity Log's found chip (2026-10-29): a read that came back empty
// while the OMS API is still working (getErpId before the RMA's number
// exists) says "not ready yet", never "found". An older entry of that read
// (logged before the flag) with no response is not ready either.
export function foundChipLabel(call) {
  if (call?.notReady) return 'not ready yet'
  if (/^getErpRmaId\b/.test(String(call?.label ?? '')) && (call?.response == null || call?.response === '')) return 'not ready yet'
  return call?.found ? 'found' : 'not found'
}

// What a row waits on, in words (Ops → Waiting on OMS API).
export function waitingOnText(row) {
  if (row?.kind === PENDING_KIND_RMA_NUMBER) return 'RMA number'
  return `$0 RMA (reason ${row?.reasonCode ?? '?'})${zeroDollarRowSendsLabel(row) ? '' : ', no label'}`
}

// A replacement's own "Create Return Shipping Label" step
// (create_return_label_for_replacement) while its process_replacement's
// optional $0 RMA label step is on (Jenn, 2026-10-29; off by default since
// 2026-10-30): hidden, and never processed, so the label comes only from the
// $0 RMA. Off, the step shows.
export function replacementLabelHeldForZeroDollar(action, actions = [], actionInputs = {}) {
  if (action?.type !== 'create_return_label_for_replacement') return false
  const sib = (actions ?? []).findIndex((a) => a?.type === 'process_replacement')
  if (sib < 0) return false
  const input = actionInputs?.[sib]
  return Boolean(zeroDollarOption(actions[sib]?.inputOptions, input?.reasonCode)) && zeroDollarLabelChecked(input)
}

// A $0 RMA replacement's own label step, optional (Jenn, 2026-10-30):
// shown unchecked under a $0 RMA code while the $0 RMA label is off; only a
// person's check lets it run (Process, Process all). Held while the $0 RMA
// label is on, so never two labels; both off, no label at all.
export function replacementLabelStepOptional(action, actions = [], actionInputs = {}) {
  if (action?.type !== 'create_return_label_for_replacement') return false
  const sib = (actions ?? []).findIndex((a) => a?.type === 'process_replacement')
  if (sib < 0) return false
  const input = actionInputs?.[sib]
  return Boolean(zeroDollarOption(actions[sib]?.inputOptions, input?.reasonCode)) && !zeroDollarLabelOn(input)
}

// The label step is optional and nobody checked it: it makes no label.
export function replacementLabelNotRequested(action, index, actions = [], actionInputs = {}) {
  return replacementLabelStepOptional(action, actions, actionInputs) && !replacementLabelOn(actionInputs?.[index])
}

// The label step's input after its optional box changes: on records who and
// when, off clears them.
export function withReplacementLabelChoice(input, checked, { by = null, at = new Date().toISOString() } = {}) {
  const next = { ...(input ?? {}) }
  if (checked) {
    next.replacementLabel = true
    next.replacementLabelBy = by
    next.replacementLabelAt = at
  } else {
    delete next.replacementLabel
    delete next.replacementLabelBy
    delete next.replacementLabelAt
  }
  return next
}

// The shadow panel's rows (step previews, server/src/ai/shadow-step-
// previews.js): the replacement's label row is hidden while the replacement
// row's optional $0 RMA label step is on. valuesByKey: the rows' live values.
const SHADOW_REPLACEMENT_TYPES = ['process_replacement', 'create_replacement_trq', 'inr_create_replacement_trq']
export function isShadowReplacementRow(preview) {
  return SHADOW_REPLACEMENT_TYPES.includes(preview?.type)
}
export function shadowReplacementLabelHeld(preview, rows = [], valuesByKey = {}) {
  if (preview?.type !== 'create_return_label_for_replacement') return false
  const repl = (rows ?? []).find(isShadowReplacementRow)
  if (!repl) return false
  const values = valuesByKey?.[repl.key] ?? repl.values ?? {}
  return Boolean(zeroDollarOption(repl.options?.reasonCodes, values.reasonCode)) && zeroDollarLabelOn(values)
}

// The steps whose chosen reason code is a $0 RMA code with the optional
// label step turned on,
// as one key ("2:95"), for seeding the draft's label sentence once per code.
// Empty when there are none.
export function zeroDollarSeedKey(actions = [], actionInputs = {}) {
  return (actions ?? [])
    .map((a, i) => (zeroDollarOption(a?.inputOptions, actionInputs?.[i]?.reasonCode) && zeroDollarLabelChecked(actionInputs?.[i]) ? `${i}:${actionInputs[i].reasonCode}` : ''))
    .filter(Boolean)
    .join(',')
}

